import { el, clear, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import { pageHeader, callout } from "../ui/components";
import type { PageRenderer } from "../ui/router";

interface Answer {
  pick: string;
  href: string;
  why: string;
  also?: string;
}

const QUESTION = "What do you want to ask about a huge stream of items?";

const OPTIONS: Array<{ id: string; title: string; desc: string; answer: Answer }> = [
  {
    id: "member",
    title: "Have I seen this item before?",
    desc: "Membership: reject things you've already processed, seen, or blocked.",
    answer: {
      pick: "Bloom filter",
      href: "#/bloom",
      why: "A Bloom filter answers “definitely not present” or “probably present” in a few bits per item, with a tunable false-positive rate and never a false negative.",
      also: "Need to delete items, or want less space at very low false-positive rates? Use a Cuckoo filter →/cuckoo.",
    },
  },
  {
    id: "distinct",
    title: "How many distinct items are there?",
    desc: "Cardinality: unique visitors, distinct IPs, distinct search terms.",
    answer: {
      pick: "HyperLogLog",
      href: "#/hyperloglog",
      why: "HyperLogLog counts distinct items to within ~1% using a fixed ~12 KB — whether the true count is a thousand or a trillion — and two sketches merge into a union for free.",
    },
  },
  {
    id: "freq",
    title: "How often does each item appear?",
    desc: "Frequency & heavy hitters: top URLs, hot keys, trending terms.",
    answer: {
      pick: "Count-Min Sketch",
      href: "#/count-min",
      why: "A Count-Min Sketch estimates per-item frequencies in a small fixed grid. It never undercounts, and its overcount is bounded by ε·N — so heavy hitters stay accurate.",
    },
  },
  {
    id: "topk",
    title: "Which items are the most frequent?",
    desc: "Heavy hitters: top URLs, noisiest errors, hottest keys — ranked.",
    answer: {
      pick: "Top-K (Space-Saving)",
      href: "#/top-k",
      why: "Space-Saving surfaces the top-K items from a stream using only K counters, guaranteeing that anything above N/K frequency is tracked. Unlike Count-Min, you don't need to name candidates — it finds them.",
    },
  },
  {
    id: "quantile",
    title: "What are the percentiles (p50/p95/p99)?",
    desc: "Distribution & tails: latency SLOs, response sizes, durations.",
    answer: {
      pick: "DDSketch",
      href: "#/dd-sketch",
      why: "DDSketch tracks quantiles over a stream within a guaranteed relative error, using a few hundred geometric buckets. It merges, so per-host p99s roll up into a global one with no re-scan.",
    },
  },
  {
    id: "similar",
    title: "How similar are these sets / documents?",
    desc: "Similarity & near-duplicates: dedup, clustering, plagiarism, recommendations.",
    answer: {
      pick: "MinHash (+ LSH)",
      href: "#/minhash",
      why: "MinHash turns each set into a short signature whose agreement estimates Jaccard similarity. Add LSH banding and you can find near-duplicates among millions without comparing every pair.",
    },
  },
  {
    id: "sample",
    title: "Can I keep a fair random sample?",
    desc: "Sampling: a uniform sample of a stream whose length you don't know.",
    answer: {
      pick: "Reservoir sampling",
      href: "#/reservoir",
      why: "Reservoir sampling keeps k uniformly-random items in one pass and O(k) memory, even from an infinite stream — every item seen so far is equally likely to be held.",
    },
  },
];

export const renderHome: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Probably · an interactive lab",
      title: "Which probabilistic data structure do I need?",
      lede: "When a stream is too big to store, you can still answer questions about it — approximately, in kilobytes, with math-backed error bars. Pick a question to get a recommendation, then open its lab to play with it.",
    }),
  );

  append(
    outlet,
    callout(
      "info",
      el(
        "div",
        {},
        el("strong", {}, "The core trade. "),
        "These structures give up exact answers in exchange for enormous memory savings and speed. In return you get a ",
        el("strong", {}, "predictable, tunable error"),
        " — not a random guess. Every page here lets you dial that trade and watch the estimate track the truth.",
      ),
    ),
  );

  // --- Interactive decision wizard ---
  const choice = signal<string | null>(null);
  const wizard = el("section", { class: "card" });

  effect(() => {
    clear(wizard);
    const picked = choice.value;
    if (!picked) {
      append(
        wizard,
        el("h3", { class: "card-title" }, QUESTION),
        el(
          "p",
          { class: "card-subtitle" },
          "One question is usually enough to narrow it down.",
        ),
        el(
          "div",
          { class: "wizard-options" },
          ...OPTIONS.map((o) =>
            el(
              "button",
              { class: "wizard-option", type: "button", onclick: () => (choice.value = o.id) },
              el("div", { class: "wo-title" }, o.title),
              el("div", { class: "wo-desc" }, o.desc),
            ),
          ),
        ),
      );
    } else {
      const opt = OPTIONS.find((o) => o.id === picked)!;
      const a = opt.answer;
      append(
        wizard,
        el("div", { class: "card-subtitle" }, opt.title),
        el(
          "div",
          { class: "answer" },
          el("div", { class: "a-pick" }, "Use a " + a.pick),
          el("p", { style: { margin: "10px 0 0", color: "var(--text-dim)" } }, a.why),
          a.also
            ? el(
                "p",
                { class: "mini-note", style: { marginTop: "10px" } },
                renderAlso(a.also),
              )
            : null,
          el(
            "div",
            { class: "button-row", style: { marginTop: "16px" } },
            el("a", { class: "btn btn-primary", href: a.href }, `Open the ${a.pick} lab →`),
            el(
              "button",
              { class: "btn btn-ghost", type: "button", onclick: () => (choice.value = null) },
              "← Ask again",
            ),
          ),
        ),
      );
    }
  });
  outlet.appendChild(wizard);

  // --- Browse-all link cards ---
  append(
    outlet,
    el("h2", { class: "prose", style: { fontSize: "1.3rem", margin: "36px 0 4px", color: "var(--text)" } }, "Or browse the whole family"),
    el(
      "div",
      { class: "card-links" },
      ...OPTIONS.map((o) =>
        el(
          "a",
          { class: "link-card", href: o.answer.href },
          el("div", { class: "lc-q" }, o.title),
          el("div", { class: "lc-title" }, o.answer.pick),
          el("div", { class: "lc-desc" }, shortDesc(o.id)),
        ),
      ),
    ),
  );

  // --- Comparison table ---
  append(outlet, comparisonTable());

  append(
    outlet,
    callout(
      "tip",
      el(
        "div",
        {},
        "Everything here runs entirely in your browser on real implementations from ",
        el("code", { class: "kbd" }, "src/core"),
        " — the same code the test suite checks against exact ground truth. Nothing is faked for the demo.",
      ),
    ),
  );
};

function renderAlso(text: string): (Node | string)[] {
  // Turn a trailing "→/path" reference into a link.
  const m = text.match(/^(.*)→(\/\S+)\.?$/);
  if (!m) return [text];
  return [m[1], el("a", { href: `#${m[2]}` }, "the Cuckoo filter"), "."];
}

function shortDesc(id: string): string {
  switch (id) {
    case "member":
      return "Approximate set membership in a few bits per item. No false negatives.";
    case "distinct":
      return "Cardinality of a stream in fixed kilobytes, from thousands to trillions.";
    case "freq":
      return "Per-item frequency estimates that never undercount; great for heavy hitters.";
    case "topk":
      return "The top-K hottest items from a stream, using only K counters.";
    case "quantile":
      return "Streaming percentiles (p50/p95/p99) within a guaranteed relative error.";
    case "similar":
      return "Jaccard similarity from short signatures; LSH finds near-duplicates at scale.";
    case "sample":
      return "A uniform random sample of an unbounded stream in one pass.";
    default:
      return "";
  }
}

function comparisonTable(): HTMLElement {
  const rows = [
    ["Bloom filter", "Is x in the set?", "False positives", "No", "~1.2 KB / 1k items @ 1%"],
    ["Cuckoo filter", "Is x in the set?", "False positives", "Yes", "Similar / smaller at low FPR"],
    ["HyperLogLog", "How many distinct?", "±% of the count", "—", "~12 KB, any cardinality"],
    ["Count-Min Sketch", "How often is x?", "Overcount ≤ ε·N", "—", "Grid of w×d counters"],
    ["Top-K (Space-Saving)", "Which items are hot?", "Count in [c−ε, c]", "—", "K counters"],
    ["DDSketch", "What's the p99?", "±α relative", "—", "~hundreds of buckets"],
    ["MinHash", "How similar are A, B?", "±√(J(1−J)/k)", "—", "k values per set"],
    ["Reservoir", "A fair sample?", "None (exact sample)", "—", "k items"],
  ];
  const table = el(
    "table",
    { class: "cmp" },
    el(
      "thead",
      {},
      el(
        "tr",
        {},
        ...["Structure", "Answers", "Error", "Delete?", "Typical memory"].map((h) =>
          el("th", {}, h),
        ),
      ),
    ),
    el(
      "tbody",
      {},
      ...rows.map((r) => el("tr", {}, ...r.map((c, i) => el("td", {}, i === 4 ? el("span", { class: "mini-note" }, c) : c)))),
    ),
  );
  return el(
    "div",
    {},
    el("h2", { style: { fontSize: "1.3rem", margin: "40px 0 4px" } }, "At a glance"),
    table,
  );
}
